<template>
  <Card v-bind="$attrs" v-on="$listeners" class="residentials-list">
    <template #default>
      <div v-show="items.length" v-if="$store.getters['Project/hasRole']('manager')" class="residentials-list__actions">
        <StatusFilter v-if="saleTypes.length" v-model="filter.hideSold" :label="statusLabel" storageKey="units:filter">
        </StatusFilter>
        <ButtonComponent
          @click="create = true"
          :label="$t('createResource', { resource: $tc('unit', 1).toLowerCase() })"
          icon="fa-pro-light:plus"
          icon-placement="left"
        />
        <DropdownComponent v-if="showSettings" :close-on-click="true">
          <ButtonComponent :label="$tc('adaptation', 2)" icon="fa-pro-light:sliders" />
          <template #dropdown>
            <ButtonComponent
              v-for="item in ['list', 'order']"
              class="units-settings__trigger"
              :label="$t(`unitsAdaptationsTrigger${Capitalize(item)}`)"
              @click="settingsVariant = item"
            >
            </ButtonComponent>
          </template>
        </DropdownComponent>
        <SettingsComponent v-if="showSettings" :variant.sync="settingsVariant"></SettingsComponent>
      </div>
      <div class="residentials-list__scroller">
        <DataTable
          theme="border"
          sticky
          :selected.sync="selected"
          :value="items"
          :columns="[
            { id: 'number', label: $t('number') },
            { id: 'slot' },
            { id: 'unit', label: $tc('unitCatalog', 1), size: '1fr', align: 'end' },
            { id: 'status', label: $t('status'), align: 'end' },
            { id: 'ownerReserved' },
            { id: 'price', label: $t('price'), align: 'end' },
          ]"
          :row-wrapper="
            (item, row) => ({
              component: 'div',
              props: {
                'data-property-type': `${
                  ((item.properties || []).find(property => property.key === 'propertyType') || {}).value
                }`,
              },
            })
          "
          :cell-wrapper="
            (item, col) =>
              !['slot', 'ownerReserved'].includes(col.id)
                ? {
                    component: $store.getters['Project/hasRole']('manager') ? 'router-link' : 'div',
                    props: {
                      to: { name: 'residential-edit', params: { id: item.id } },
                      style: { color: 'inherit', textDecoration: 'inherit' },
                    },
                  }
                : { component: 'div' }
          "
        >
          <template #number="{ item }">{{ item.number }}</template>
          <template #unit="{ item }">{{ $path('template.internalName', item) }}</template>
          <template #slot="{ item }">
            <SlotComponent placement="residential:list" :query="{ residential: item.id }" />
          </template>
          <template #price="{ item }">
            {{ getPrice(item) }}
          </template>
          <template #ownerReserved="{ item }">
            <Avatar v-if="getAvatars(item).length" :value="getAvatars(item)" size="small" @click="onAvatarClick" />
          </template>
          <template #status="{ item }">
            <Badge
              :value="$t(`residentialStatus:${item.sale.type}.${item.status}`)"
              :theme="getStatusTheme(item.status)"
              :type="item.status === 'draft' ? 'outlined' : 'default'"
            />
          </template>
          <template #primary-row-actions="{ item }">
            <ButtonComponent
              v-if="$store.getters['Project/hasRole'](['manager', 'complaint.manager'])"
              tag="router-link"
              v-tooltip="$t('residentialAccount')"
              :icon="['fal', 'home']"
              :to="{ name: 'residential-edit', params: { id: item.id, step: 'residential-account' } }"
            />
            <ButtonComponent
              v-if="addons.includes('project:complaint') && $store.getters['Project/hasRole']('complaint.manager')"
              tag="router-link"
              v-tooltip="$tc('complaint', 2)"
              :icon="['fal', 'tasks']"
              :to="{
                name: 'residential-complaints',
                params: { id: item.id },
              }"
            />
          </template>
          <template #secondary-row-actions="{ item, visible }">
            <ButtonComponent
              v-if="$store.getters['Project/hasRole']('manager')"
              tag="router-link"
              :label="$t('edit')"
              :icon="['fal', 'pen']"
              :to="{ name: 'residential-edit', params: { id: item.id } }"
            />
            <ButtonComponent
              v-if="$store.getters['Project/hasRole'](/^shop/)"
              tag="router-link"
              :label="$tc('addonShop', 1)"
              :icon="['fal', 'ruler-triangle']"
              :to="{ name: 'residential-edit', params: { id: item.id, step: 'shop' } }"
            />
            <ButtonComponent
              v-if="$store.getters['Project/hasRole'](/signingAuthority$/)"
              tag="router-link"
              :label="$tc('document', 2)"
              :icon="['fal', 'file-contract']"
              :to="{
                name: 'residential-contracts',
                params: { id: item.id },
              }"
            />
            <ButtonComponent tag="a" :href="item.url" target="_blank" :label="$t('preview')" :icon="['fal', 'eye']" />
            <ButtonComponent
              v-if="$store.getters['Project/hasRole'](['manager', 'complaint.manager'])"
              tag="router-link"
              :label="$t('residentialAccount')"
              :icon="['fal', 'home']"
              :to="{ name: 'residential-edit', params: { id: item.id, step: 'residential-account' } }"
            />
            <ButtonComponent
              v-if="addons.includes('project:complaint') && $store.getters['Project/hasRole']('complaint.manager')"
              tag="router-link"
              :label="$tc('complaint', 2)"
              :icon="['fal', 'tasks']"
              :to="{
                name: 'residential-complaints',
                params: { id: item.id },
              }"
            />
            <Actions v-if="visible && project" placement="residential" :query="{ residential: item.id }" type="list" @action:refetch="reset" />
          </template>
          <template #select-actions>
            <ButtonComponent
              v-if="$store.getters['Project/hasRole']('manager')"
              :label="$t('edit')"
              :icon="['fal', 'pen']"
              @click="update = true"
            />
            <div v-if="$store.getters.isSuperadmin" v-tooltip="!canRemove(selected) && $t('cannotDeleteBusyUnit')">
              <ButtonComponent
                :label="$t('delete')"
                :disabled="!canRemove(selected)"
                :icon="['fal', 'trash']"
                @click="context = selected"
              />
            </div>
          </template>
        </DataTable>
        <InfiniteLoading v-if="project.id" @infinite="next" :identifier="infiniteId">
          <template #spinner>
            <div class="infinite-loading-spinner">
              <span class="infinite-loading-spinner--text">{{ $t('loading', { resource: '...' }) }}</span>
              <FontAwesomeIcon
                class="infinite-loading-spinner--spinner"
                :icon="['fal', 'spinner-third']"
                spin
                size="2x"
              />
            </div>
          </template>
          <template #no-more>{{ $t('noMoreUnits') }}</template>
          <template #no-results>
            <EmptyState :title="$t('noX', { x: $tc('unit', 2).toLowerCase() })" :icon="['fal', 'home']">
              <template #actions>
                <ButtonComponent
                  :label="$t('projectShopsCreateResidentials')"
                  theme="secondary"
                  :icon="['fal', 'plus']"
                  tag="router-link"
                  class="text--small"
                  :to="{
                    name: 'project-edit',
                    params: { id: project.id, step: 'residentials' },
                    query: { create: true },
                  }"
                />
              </template>
            </EmptyState>
          </template>
        </InfiniteLoading>
      </div>
      <CreateResidential
        v-if="project.id"
        :show="create"
        :number-pattern="$path('metadata.residentialNumberPattern', project)"
        :project="project.id"
        @close="create = false"
        @input="add"
      />
      <RemoveComponent
        :show.sync="removeDialog"
        :title="context.map(c => $path('number', c)).join('<br />')"
        :action="() => remove()"
        @onSuccess="onRemove"
      />
      <UpdateComponent
        v-if="update"
        @close="update = false"
        @done="onUpdateDone"
        :residentials="selected"
        :disabledFields="$path('metadata.disabledFields.unit', project)"
        :project="project"
      />
    </template>
  </Card>
</template>

<script>
import { mapState } from 'vuex'
import Bus from '@/bus'
import { Currency } from '@/filters'
import API from '@/api/Residential'
import { ToArray, Initials, Capitalize } from '@/utils'
import Pagination from '@/mixins/Pagination'

import Card from '@/components/Card'
import RemoveComponent from '@/components/Residential/Remove'
import UpdateComponent from '@/components/Residential/Update'
import SettingsComponent from '@/components/Residential/Settings/index.js'

import CreateResidential from '@/components/Residential/CreateModal'
import InfiniteLoading from 'vue-infinite-loading'
import Actions from '@/components/Actions'
import DataTable from '@/components/DataTable'
import Badge from '@/components/Badge'
import EmptyState from '@/components/EmptyState'
import SlotComponent from '@/components/Slot'
import tracker from '@/tracking'
import Avatar from '@/components/Avatar'
import { DropdownComponent } from 'vue-elder-dropdown'

import StatusFilter from '@/components/Project/StatusFilter'

export default {
  mixins: [Pagination],
  watch: {
    filter: {
      handler: 'reset',
      deep: true,
    },
  },
  props: {
    showSettings: Boolean,
  },
  data() {
    return {
      update: false,
      filter: {
        hideSold: false,
      },
      context: [],
      settingsVariant: '',
    }
  },
  computed: {
    ...mapState('Project', {
      saleTypes(state) {
        return this.$path('metadata.residentialProperties.saleType', state.item) || []
      },
      project(state) {
        return state.item
      },
    }),
    statusLabel() {
      if (this.saleTypes.length === 2) return this.$tc('residentialListStatusHideFilter', 2)
      if (this.saleTypes.includes('rent')) return this.$tc('residentialListStatusHideFilter', 1)
      return this.$tc('residentialListStatusHideFilter', 0)
    },
    addons() {
      return this.$path('metadata.addons', this.project) || []
    },
    items() {
      return this.item.docs
    },
    isLast() {
      return this.item.page === this.item.pages
    },
    removeDialog: {
      get() {
        return Boolean(this.context && this.context.length)
      },
      set(val) {
        if (!val) this.context = []
      },
    },
    create: {
      get() {
        return ['true', true].includes(this.$route.query.create)
      },
      set(val) {
        this.$router.replace({ query: { ...this.$route.query, create: val ? true : undefined } })
      },
    },
  },
  methods: {
    Capitalize,

    getAvatars(item) {
      const getAction = contact => {
        const lead = this.$path('leads.0.id', contact)
        return lead
          ? { name: 'project-lead-edit', params: { reference: this.project.id, id: lead } }
          : { name: 'contact-dashboard', params: { id: contact.id } }
      }

      switch (item.status) {
        case 'reserved':
          return item.reservedBy.map(e => ({
            name: e.name,
            src: `/api/avatar/${Initials(e.name)}`,
            action: getAction(e),
          }))
        case 'sold':
        case 'rented':
          return item.owners.map(e => ({ name: e.name, src: `/api/avatar/${Initials(e.name)}`, action: getAction(e) }))
        default:
          return []
      }
    },
    onAvatarClick(item) {
      if (!item.action) return

      this.$router.push(item.action)
    },
    canRemove(item) {
      return !ToArray(item).some(c => ['sold', 'rented'].includes(c.status))
    },
    remove() {
      if (!this.context) return

      return Promise.all(this.context.map(c => this.$store.dispatch('Residential/remove', { id: c.id })))
    },
    onRemove() {
      this.reset()
    },
    onUpdateDone() {
      this.update = false
    },
    fetch() {
      if (this.item.pages === 0 || (this.item.pages && this.item.page === this.item.pages)) return Promise.resolve()

      return API.get(
        {
          paginate: this.paginate,
          filter: {
            project: this.project.id,
            ...(this.filter.hideSold ? { status: ['!sold', '!rented'] } : {}),
          },
          leadReferences: [this.project.id],
        },
        {
          query: 'ProjectResidentials',
        },
      ).then(data => {
        tracker.emit('ProjectResidentials:paginate', data)
        return data
      })
    },
    add(data) {
      this.reset()
    },
    getPrice(item) {
      let price = Currency(this.$path('sale.value', item), { currency: this.project.metadata.currency })
      switch (item.sale.type) {
        case 'rent':
          if ([null, undefined].includes(price)) return this.$t('priceOnRequest')
          return `${price} / ${this.$t('multiplicatorMonthly')}.`
        default:
          if ([null, undefined].includes(price)) return this.$t('priceOnRequest')
          return price
      }
    },
    getStatusTheme(status) {
      switch (status) {
        case 'sale':
        case 'rent':
          return 'success'
        case 'sold':
        case 'rented':
          return 'error'
        case 'reserved':
          return 'warning'
        default:
          return 'default'
      }
    },
  },
  created() {
    Bus.$on('residentials:create', state => (this.create = state))
  },
  components: {
    Card,
    CreateResidential,
    RemoveComponent,
    UpdateComponent,
    SettingsComponent,
    DropdownComponent,
    InfiniteLoading,
    Actions,
    DataTable,
    Badge,
    EmptyState,
    StatusFilter,
    SlotComponent,
    Avatar,
  },
}
</script>

<style lang="scss">
.residentials-list {
  height: 100%;
  overflow: hidden;

  & > .card__content {
    padding: 0;
  }

  &__scroller {
    height: 501px;
    padding: 0rem var(--card-spacing);
    overscroll-behavior: contain;
    overflow-y: auto;
    overflow-x: auto;

    @include scrollShadow();
    @include respond-below('phone') {
      max-width: calc(100vw - 8rem);
    }
  }

  &__actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    justify-content: flex-end;
    padding: var(--card-spacing) var(--card-spacing) 0 var(--card-spacing);
    font-size: var(--font-size-small);
  }

  .data-table__row:has([data-cell-id='slot']:empty) {
    [data-cell-id='number'] {
      grid-column-end: span 2;
    }
    [data-cell-id='slot'] {
      display: none;
    }
  }

  .infinite-loading-spinner {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    &--text {
      opacity: 0.7;
    }
    &--spinner {
      color: hsla(var(--primary-h), var(--primary-s), var(--primary-l), 0.5);
    }
  }

  .infinite-status-prompt {
    margin-bottom: var(--card-spacing);
  }

  .avatar {
    cursor: pointer;
  }
}
</style>
